import { ComponentRendering } from '@sitecore-jss/sitecore-jss-react';
import {
  TCHHeroBannerHeroAsset,
  TContentHubHeroBannerSeriesDetailFields,
  TContentHubHeroBannerSeriesDetailModelSummarySpecs,
  TContentHubHeroBannerSeriesDetailProps,
  TContentHubHeroBannerSeriesDetailTextColorParams,
} from '../types/productHeroBannerSeriesDetail.types';
import { TContentHubButton, TRendition } from '../../../types/types';
import { appendRootToInternalLink } from '../../../utils/appendRootToInternalLink.utils';

export class ProductHeroBannerSeriesDetailModel {
  private readonly fields?: TContentHubHeroBannerSeriesDetailFields;
  private readonly params?: TContentHubHeroBannerSeriesDetailTextColorParams;
  private readonly rendering?: ComponentRendering;

  constructor(props: TContentHubHeroBannerSeriesDetailProps) {
    this.fields = props?.fields;
    this.params = props?.params;
    this.rendering = props?.rendering;
  }

  get thumbnailEE(): string {
    return this.fields?.itemData?.thumbnail || '';
  }

  get componentName(): string {
    return this.rendering?.componentName || '';
  }

  get title(): string | undefined {
    return this.fields?.apiData?.title;
  }

  get msrp(): string | undefined {
    return this.fields?.apiData?.msrp;
  }

  get msrpTitle(): string | undefined {
    return this.fields?.apiData?.msrpTitle;
  }

  get eyebrow(): string | undefined {
    return this.fields?.apiData?.eyebrow;
  }

  // eslint-disable-next-line @typescript-eslint/camelcase
  get rendition_desktop_size1200(): TRendition | undefined {
    return this.fields?.apiData?.assets?.[0]?.rendition_desktop_size1200;
  }

  // eslint-disable-next-line @typescript-eslint/camelcase
  get rendition_desktop_size1900(): TRendition | undefined {
    return this.fields?.apiData?.assets?.[0]?.rendition_desktop_size1900;
  }

  // eslint-disable-next-line @typescript-eslint/camelcase
  get rendition_mobile_size1100(): TRendition | undefined {
    return this.fields?.apiData?.assets?.[0]?.rendition_mobile_size1100;
  }

  // eslint-disable-next-line @typescript-eslint/camelcase
  get rendition_tablet_size1000(): TRendition | undefined {
    return this.fields?.apiData?.assets?.[0]?.rendition_tablet_size1000;
  }

  // eslint-disable-next-line @typescript-eslint/camelcase
  get rendition_tablet_size1200(): TRendition | undefined {
    return this.fields?.apiData?.assets?.[0]?.rendition_tablet_size1200;
  }

  get image(): string | undefined {
    return this.fields?.apiData?.full;
  }

  get mobileImage(): string | undefined {
    return this.fields?.apiData?.thumbnail;
  }

  get primaryLink(): TContentHubButton | undefined {
    return this.fields?.apiData?.button1;
  }

  get primaryCTAUrl(): string | undefined {
    return appendRootToInternalLink(this.primaryLink?.url);
  }

  get secondaryLink(): TContentHubButton | undefined {
    return this.fields?.apiData?.button2;
  }

  get secondaryCTAUrl(): string | undefined {
    return appendRootToInternalLink(this.secondaryLink?.url);
  }

  get isPrimaryLinkExist(): boolean {
    return !!(this?.primaryLink?.label && this?.primaryLink?.url);
  }

  get hasSpecialOffers(): boolean {
    return !!this.fields?.apiData?.hasSpecialOffers;
  }

  get isSecondaryLinkExist(): boolean {
    return !!(this?.secondaryLink?.label && this?.secondaryLink?.url);
  }

  get summarySpecs(): Array<TContentHubHeroBannerSeriesDetailModelSummarySpecs> {
    return this.fields?.apiData?.summarySpecs || [];
  }

  get textColor(): string {
    return this.params?.textColor?.toLowerCase() || 'white';
  }

  get quickSpecsColor(): string {
    return this.params?.quickSpecsColor?.toLowerCase() || 'white';
  }

  get isUnreleased(): boolean {
    return !!this.fields?.isComponentUnreleased;
  }

  get enableBuildPriceCTA(): boolean | undefined {
    return this.params?.enableBuildPriceCTA === '1';
  }
  get assetHero(): TCHHeroBannerHeroAsset | undefined {
    return this.fields?.apiData?.heroAsset;
  }

  get heroAssetRole(): string | undefined {
    return this.fields?.apiData?.heroAsset?.role;
  }
  get tabletLandscapeHeroAsset(): TRendition | undefined {
    return this.fields?.apiData?.heroAsset?.rendition_tablet_size1000 as TRendition;
  }

  get tabletPotraitHeroAsset(): TRendition | undefined {
    return this.fields?.apiData?.heroAsset?.rendition_tablet_size1200 as TRendition;
  }

  get mobileImageHeroAsset(): TRendition | undefined {
    return this.fields?.apiData?.heroAsset?.rendition_mobile_size1100 as TRendition;
  }

  get desktopImageHeroAsset(): TRendition | undefined {
    return this.fields?.apiData?.heroAsset?.rendition_desktop_size1900;
  }

  get desktopSDImageHeroAsset(): TRendition | undefined {
    return this.fields?.apiData?.heroAsset?.rendition_desktop_size1200 as TRendition;
  }
}
